package com.parmartechnologies.storage.database

import com.parmartechnologies.storage.collection.DatabaseCollectionConfig
import com.parmartechnologies.storage.collection.DatabaseCollectionHelper
import com.parmartechnologies.storage.constant.KeyConstant.Companion.id
import com.parmartechnologies.storage.constant.KeyConstant.Companion.name
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import org.w3c.dom.Window
import kotlin.js.json

object IndexDatabase {
    val serializer = Json {
        encodeDefaults = true
        ignoreUnknownKeys = true
        isLenient = true
    }
    private const val DB_NAME = "${DatabaseConfig.NAME}_1_${DatabaseConfig.VERSION}"
    private const val DB_VERSION = DatabaseConfig.VERSION
    private val storeIndexJs = js("{keyPath: '_id'}")

    fun openDB(window: Window, onReady: () -> Unit) {
        val request = window.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            console.log("Database Open")
            DatabaseCollectionConfig().getAllCollectionName().forEach { collectionName ->
                val db = event.target.result
                if (!db.objectStoreNames.contains(collectionName)) {
                    console.log("Creating collection store : $collectionName")
                    db.createObjectStore(collectionName, storeIndexJs)
                }
            }
        }
        request.onsuccess = {
            onReady()
            console.log("Database opened")
        }
        request.onerror = {
            console.log("Failed to open DB:", request.error)
        }
    }

    fun <T> storeSerializable(
        value: T,
        serializer: kotlinx.serialization.KSerializer<T>
    ) {
        val collectionName = DatabaseCollectionHelper().getCollectionName(serializer = serializer)
        val request = AppDatabase.window!!.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            val db = event.target.result
            if (!db.objectStoreNames.contains(collectionName)) {
                db.createObjectStore(collectionName, storeIndexJs)
            }
        }
        request.onsuccess = { event: dynamic ->
            console.log("Trying to write in collection store : $collectionName")
            val db = event.target.result
            val tx = db.transaction(collectionName, "readwrite")
            val store = tx.objectStore(collectionName)
            val json = Json.encodeToString(serializer, value)
//            val id = DatabaseCollectionHelper().getValueForKey("_id",value,serializer)
            val jsonObject = JSON.parse<dynamic>(json)
            store.put(jsonObject)
        }
        request.onerror = {
            console.log("Failed to write in store: $collectionName :", request.error)
        }
    }

    fun <T> loadSerializable(
        key: String,
        serializer: kotlinx.serialization.KSerializer<T>,
        onResult: (T?) -> Unit
    ) {
        val collectionName = DatabaseCollectionHelper().getCollectionName(serializer = serializer)
        val request = AppDatabase.window!!.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            val localDatabase = event.target.result
            if (!localDatabase.objectStoreNames.contains(collectionName)) {
                localDatabase.createObjectStore(collectionName, storeIndexJs)
            }
        }
        request.onsuccess = { event: dynamic ->
            console.log("Trying to read from collection store : $collectionName")
            val db = event.target.result
            val tx = db.transaction(collectionName, "readonly")
            val store = tx.objectStore(collectionName)
            val getRequest = store.get(key)
            getRequest.onsuccess = {
                val json = getRequest.result as? String
                if (json != null) {
                    onResult(Json.decodeFromString(serializer, json))
                } else {
                    onResult(null)
                }
            }
            getRequest.onerror = {
                console.log(
                    "Failed to read get request in store: $collectionName :",
                    getRequest.error
                )
            }
        }
        request.onerror = {
            console.log("Failed to write in store: $collectionName :", request.error)
        }
    }


    fun <T> loadAllSerializable(
        serializer: kotlinx.serialization.KSerializer<T>,
        onResult: (List<T>?) -> Unit
    ) {
        val collectionName = DatabaseCollectionHelper().getCollectionName(serializer = serializer)
        val request = AppDatabase.window!!.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            val localDatabase = event.target.result
            if (!localDatabase.objectStoreNames.contains(collectionName)) {
                localDatabase.createObjectStore(collectionName, storeIndexJs)
            }
        }
        request.onsuccess = { event: dynamic ->
            val db = request.result
            val tx = db.transaction(collectionName, "readonly")
            val store = tx.objectStore(collectionName)
            val cursorRequest = store.getAll()
            val results = mutableListOf<T>()
            cursorRequest.onsuccess = {
                val cursor: Array<String> = cursorRequest.result
                CoroutineScope(Job() + Dispatchers.Main).launch {
                    for (arrayItem in cursor) {
                        console.log("single value ", arrayItem)
                        val value = Json.decodeFromString(serializer, arrayItem)
                        results.add(value)
                    }
                    onResult(results)
                }
            }
            cursorRequest.onerror = {
                console.log(
                    "Failed to read cursor in store: $collectionName :",
                    cursorRequest.error
                )
            }
        }
        request.onerror = {
            console.log("Failed to write in store: $collectionName :", request.error)
        }
    }

    fun <T> loadAllSerializable(
        serializer: kotlinx.serialization.KSerializer<T>,
        andQuery: Map<String, Any?>,
        onResult: (MutableList<String>) -> Unit
    ) {
        val collectionName = DatabaseCollectionHelper().getCollectionName(serializer = serializer)
        val request = AppDatabase.window!!.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            val localDatabase = event.target.result
            if (!localDatabase.objectStoreNames.contains(collectionName)) {
                localDatabase.createObjectStore(collectionName, storeIndexJs)
            }
        }
        request.onsuccess = { event: dynamic ->
            val db = request.result
            val tx = db.transaction(collectionName, "readonly")
            val store = tx.objectStore(collectionName)
            val cursorRequest = store.getAll()
            val results = mutableListOf<String>()
            cursorRequest.onsuccess = {
                val cursor: Array<String> = cursorRequest.result
                CoroutineScope(Job() + Dispatchers.Main).launch {
                    for (arrayItem in cursor) {
                        console.log("single value ", arrayItem)
                        var existingValues = 0
                        andQuery.forEach {ke->
                            if(arrayItem.toString().contains(ke.value.toString())){
                                existingValues +=1
                            }
                        }
                        if(existingValues == andQuery.keys.count()){
                            results.add(arrayItem)
                        }
                    }
                    onResult(results)
                }
            }
            cursorRequest.onerror = {
                console.log(
                    "Failed to read cursor in store: $collectionName :",
                    cursorRequest.error
                )
            }
        }
        request.onerror = {
            console.log("Failed to write in store: $collectionName :", request.error)
        }
    }

    fun <T> loadAllSerializable(
        id: String,
        serializer: kotlinx.serialization.KSerializer<T>,
        onResult: (List<T>?) -> Unit
    ) {
        val collectionName = DatabaseCollectionHelper().getCollectionName(serializer = serializer)
        val request = AppDatabase.window!!.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            val db = event.target.result
            if (!db.objectStoreNames.contains(collectionName)) {
                db.createObjectStore(collectionName, storeIndexJs)
            }
        }
        request.onsuccess = { event: dynamic ->
            console.log("Trying to read from collection store : $collectionName")
            val db = request.result
            val tx = db.transaction(collectionName, "readonly")
            val store = tx.objectStore(collectionName)
            val cursorRequest = store.getAll()
            val results = mutableListOf<T>()
            cursorRequest.onsuccess = {
                val cursor: Array<dynamic> = cursorRequest.result
                CoroutineScope(Job() + Dispatchers.Main).launch {
                    for (arrayItem in cursor) {
                        val jsonString = JSON.stringify(arrayItem)
                        console.log("single value ", arrayItem)
                        val value = Json.decodeFromString(serializer, jsonString)
                        if (jsonString.contains(id)) {
                            results.add(value)
                        }
                    }
                    onResult(results)
                }
            }
            cursorRequest.onerror = {
                console.log(
                    "Failed to read cursor in store: $collectionName :",
                    cursorRequest.error
                )
            }
        }
        request.onerror = {
            console.log("Failed to write in store: $collectionName :", request.error)
        }
    }

    fun delete(name: String, id: String) {
        val request = AppDatabase.window!!.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            val db = event.target.result
            if (!db.objectStoreNames.contains(name)) {
                db.createObjectStore(name, storeIndexJs)
            }
        }
        request.onsuccess = { event: dynamic ->
            console.log("Trying to read from collection store : $name")
            val db = request.result
            val tx = db.transaction(name, "readonly")
            val store = tx.objectStore(name)
            val cursorRequest = store.delete(id)
            val results: MutableList<String> = mutableListOf()
            cursorRequest.onsuccess = {
                CoroutineScope(Job() + Dispatchers.Main).launch {
                    console.log("single value deleted", results)
                }
            }
            cursorRequest.onerror = {
                console.log("Failed to read cursor in store: $name :", cursorRequest.error)
            }
        }
        request.onerror = {
            console.log("Failed to read in store: $name :", request.error)
        }
    }

    fun deleteAll(name: String) {
        val request = AppDatabase.window!!.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            val db = event.target.result
            if (!db.objectStoreNames.contains(name)) {
                db.createObjectStore(name, storeIndexJs)
            }
        }
        request.onsuccess = { event: dynamic ->
            console.log("Trying to read from collection store : $name")
            val db = request.result
            val tx = db.transaction(name, "readonly")
            val store = tx.objectStore(name)
            val cursorRequest = store.clear()
            val results: MutableList<String> = mutableListOf()
            cursorRequest.onsuccess = {
                CoroutineScope(Job() + Dispatchers.Main).launch {
                    console.log("single value deleted", results)
                }
            }
            cursorRequest.onerror = {
                console.log("Failed to read cursor in store: $name :", cursorRequest.error)
            }
        }
        request.onerror = {
            console.log("Failed to read in store: $name :", request.error)
        }
    }


    fun read(name: String, callback: (List<String>?) -> Unit) {
        val request = AppDatabase.window!!.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
        request.onupgradeneeded = { event: dynamic ->
            val db = event.target.result
            if (!db.objectStoreNames.contains(name)) {
                db.createObjectStore(name, storeIndexJs)
            }
        }
        request.onsuccess = { event: dynamic ->
            console.log("Trying to read from collection store : $name")
            val db = request.result
            val tx = db.transaction(name, "readonly")
            val store = tx.objectStore(name)
            val cursorRequest = store.getAll()
            val results: MutableList<String> = mutableListOf()
            cursorRequest.onsuccess = {
                val cursor: Array<String> = cursorRequest.result
                CoroutineScope(Job() + Dispatchers.Main).launch {
                    for (arrayItem in cursor) {
                        console.log("single value ", arrayItem)
                        results.add(arrayItem)

                    }
                    callback(results)
                }
            }
            cursorRequest.onerror = {
                console.log("Failed to read cursor in store: $name :", cursorRequest.error)
            }
        }
        request.onerror = {
            console.log("Failed to read in store: $name :", request.error)
        }
    }
}